// SearchPalette — ⌘K full-screen overlay.
// Debounced search via React Query; arrow-key + Enter navigation; recent
// searches in localStorage; per-result-type icons & navigation handlers.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  AlertOctagon,
  ClipboardList,
  Table,
  ShieldCheck,
  Building2,
  X,
  Clock,
} from "lucide-react";
import { useGlobalSearch } from "../api/sitroom";
import { useSearchPalette } from "../hooks/useSearchPalette";
import { useStore } from "../store";

const RECENT_KEY = "sitroom_recent_searches";
const MAX_RECENT = 5;

const loadRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
};

const pushRecent = (q) => {
  if (!q || q.length < 2) return;
  const next = [q, ...loadRecent().filter((x) => x !== q)].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(next));
};

const GROUP_META = {
  incidents: { label: "Incidents", Icon: AlertOctagon, color: "#ff7e7c" },
  eightds: { label: "8D Reports", Icon: ClipboardList, color: "#9ec3ec" },
  fmeas: { label: "FMEA", Icon: Table, color: "#c89adb" },
  lpas: { label: "LPA Audits", Icon: ShieldCheck, color: "#4ed084" },
  tenants: { label: "Tenants", Icon: Building2, color: "#f3b14a" },
};

const sevColorMap = {
  s1: "#E24B4A",
  s2: "#f39c12",
  s3: "#185FA5",
  s4: "#9198b0",
};

// Debounce helper (200ms)
const useDebounced = (value, delay = 200) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
};

const ResultRow = ({ group, item, active, onClick }) => {
  const meta = GROUP_META[group];
  const Icon = meta.Icon;
  // Severity badge only makes sense for incidents (string s1/s2/s3/s4).
  // FMEA / 8D may have numeric `severity` (1-10 RPN factor) — ignore there.
  const sevRaw =
    group === "incidents" && typeof item.severity === "string" ? item.severity : null;
  const sev = sevRaw ? sevColorMap[sevRaw.toLowerCase()] : null;
  const title = item.title || item.name || item.failureMode || item.auditor || item.id;
  const subtitle =
    item.parties ||
    item.partNumber ||
    item.cause ||
    item.type ||
    (item.tenant_id !== undefined ? item.tenant_id : "");

  return (
    <button
      onClick={onClick}
      className={"w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors"}
      style={{
        background: active ? "rgba(24,95,165,0.16)" : "transparent",
        borderLeft: active ? "2px solid #185FA5" : "2px solid transparent",
      }}
      data-testid={`search-result-${group}-${item.id}`}
      data-active={active ? "1" : "0"}
    >
      <Icon size={14} color={meta.color} strokeWidth={1.8} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              color: "#9198b0",
              letterSpacing: "0.3px",
            }}
          >
            {item.id}
          </span>
          <span
            className="truncate"
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 13,
              color: "#f0f2f8",
              fontWeight: 600,
            }}
          >
            {title}
          </span>
        </div>
        {subtitle && (
          <div
            className="truncate"
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              color: "#c5cad8",
              marginTop: 1,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {sev && (
        <span
          style={{
            background: `${sev}22`,
            border: `1px solid ${sev}80`,
            color: sev,
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            padding: "1px 6px",
            borderRadius: 3,
            letterSpacing: "1px",
          }}
        >
          {sevRaw.toUpperCase()}
        </span>
      )}
      {item.status && !sev && (
        <span
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.10)",
            color: "#9aa1b3",
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            padding: "1px 6px",
            borderRadius: 3,
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          {String(item.status).slice(0, 12)}
        </span>
      )}
    </button>
  );
};

export const SearchPalette = () => {
  const { isOpen, close } = useSearchPalette();
  const navigate = useNavigate();
  const openPanel = useStore((s) => s.openPanel);
  const openEightDPanel = useStore((s) => s.openEightDPanel);
  const openFmeaPanel = useStore((s) => s.openFmeaPanel);
  const openLpaPanel = useStore((s) => s.openLpaPanel);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recent, setRecent] = useState(loadRecent());
  const debounced = useDebounced(query.trim(), 200);
  const { data: results, isFetching } = useGlobalSearch(debounced, isOpen);
  const inputRef = useRef(null);

  // Flatten visible results for keyboard navigation
  const flatResults = useMemo(() => {
    if (!results) return [];
    const order = ["incidents", "eightds", "fmeas", "lpas", "tenants"];
    return order.flatMap((g) => (results[g] || []).map((item) => ({ group: g, item })));
  }, [results]);

  // Reset state when opened, focus input
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
      setRecent(loadRecent());
      setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    }
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debounced]);

  const activate = (group, item) => {
    pushRecent(debounced);
    close();
    if (group === "incidents") {
      navigate("/incidents");
      setTimeout(() => openPanel(item.id), 60);
    } else if (group === "eightds") {
      navigate("/8d");
      setTimeout(() => openEightDPanel(item.id), 60);
    } else if (group === "fmeas") {
      navigate("/fmea");
      setTimeout(() => openFmeaPanel(item.id), 60);
    } else if (group === "lpas") {
      navigate("/lpa");
      setTimeout(() => openLpaPanel(item.id), 60);
    } else if (group === "tenants") {
      navigate("/tenants");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sel = flatResults[activeIndex];
      if (sel) activate(sel.group, sel.item);
    }
  };

  if (!isOpen) return null;

  const showEmpty =
    debounced.length >= 2 && !isFetching && flatResults.length === 0;
  const showRecent = debounced.length < 2 && recent.length > 0;

  // Precompute flat-index per group for active-row highlighting
  let cursor = -1;

  return (
    <div
      className="fixed inset-0 z-[450] flex items-start justify-center pt-[12vh] px-4"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}
      onClick={close}
      data-testid="search-palette-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-lg w-full"
        style={{
          maxWidth: 600,
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
          overflow: "hidden",
        }}
        data-testid="search-palette"
      >
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <Search size={16} color="#9198b0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search incidents, 8Ds, FMEA, LPA, tenants…"
            className="flex-1 bg-transparent outline-none"
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 18,
              color: "#f0f2f8",
            }}
            data-testid="search-palette-input"
          />
          <kbd
            className="hidden sm:inline"
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 10,
              color: "#9198b0",
              padding: "2px 7px",
              border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: 4,
            }}
          >
            ESC
          </kbd>
          <button
            onClick={close}
            className="rounded-md p-1 hover:bg-white/5 sm:hidden"
            aria-label="Close palette"
          >
            <X size={14} color="#9198b0" />
          </button>
        </div>

        <div
          className="max-h-[60vh] overflow-y-auto"
          style={{ minHeight: 120 }}
          data-testid="search-palette-results"
        >
          {isFetching && debounced.length >= 2 && (
            <div className="px-4 py-3 flex flex-col gap-2" data-testid="search-shimmer">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded"
                  style={{
                    height: 28,
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 100%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s linear infinite",
                  }}
                />
              ))}
              <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
            </div>
          )}

          {!isFetching && debounced.length >= 2 && flatResults.length > 0 && (
            <div>
              {["incidents", "eightds", "fmeas", "lpas", "tenants"].map((g) => {
                const list = (results && results[g]) || [];
                if (!list.length) return null;
                const meta = GROUP_META[g];
                return (
                  <div key={g} className="pt-2 pb-1">
                    <div
                      className="px-4 py-1.5 flex items-center gap-2"
                      style={{
                        fontFamily: "DM Mono, monospace",
                        fontSize: 9,
                        letterSpacing: "1.5px",
                        color: "#9198b0",
                        textTransform: "uppercase",
                      }}
                      data-testid={`search-group-${g}`}
                    >
                      {meta.label} · {list.length}
                    </div>
                    {list.map((item) => {
                      cursor += 1;
                      const active = cursor === activeIndex;
                      return (
                        <ResultRow
                          key={`${g}-${item.id}`}
                          group={g}
                          item={item}
                          active={active}
                          onClick={() => activate(g, item)}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {showEmpty && (
            <div className="px-4 py-8 text-center" data-testid="search-empty">
              <div
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: 14,
                  color: "#f0f2f8",
                  fontWeight: 600,
                }}
              >
                No results for &ldquo;{debounced}&rdquo;
              </div>
              <div
                className="mt-2"
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 10,
                  color: "#9198b0",
                  letterSpacing: "0.5px",
                }}
              >
                Try searching by incident ID, part number, or supplier name.
              </div>
            </div>
          )}

          {showRecent && (
            <div className="py-2" data-testid="search-recent">
              <div
                className="px-4 py-1.5 flex items-center gap-2"
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 9,
                  letterSpacing: "1.5px",
                  color: "#9198b0",
                  textTransform: "uppercase",
                }}
              >
                <Clock size={11} /> Recent searches
              </div>
              {recent.map((q) => (
                <button
                  key={q}
                  onClick={() => setQuery(q)}
                  className="w-full text-left px-4 py-2 hover:bg-white/[0.04]"
                  style={{
                    fontFamily: "DM Mono, monospace",
                    fontSize: 11,
                    color: "#cfd1d8",
                  }}
                  data-testid={`search-recent-${q}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {debounced.length < 2 && recent.length === 0 && (
            <div
              className="px-4 py-8 text-center"
              style={{
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
                color: "#9198b0",
                letterSpacing: "0.5px",
              }}
              data-testid="search-hint"
            >
              Type at least 2 characters to search.
            </div>
          )}
        </div>

        <div
          className="flex items-center justify-between px-4 py-2"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            fontFamily: "DM Mono, monospace",
            fontSize: 9,
            color: "#6b7388",
            letterSpacing: "1px",
          }}
        >
          <span>↑↓ navigate · ↵ open · ESC close</span>
          <span>SITROOM</span>
        </div>
      </div>
    </div>
  );
};
